import React, { useEffect, useState } from 'react';
import { Pencil, X } from '@phosphor-icons/react';

// In-app prompt modal. Drop-in replacement for window.prompt() — the native
// version is silently suppressed by Brave and by Firefox when the user has
// ticked "prevent this page from creating additional dialogs", which makes
// destructive or input-required actions appear broken.
//
// Usage:
//   const [open, setOpen] = useState(false);
//   <PromptDialog
//     open={open}
//     title="Name this order"
//     message="A YYYY-MM-DD prefix will be added automatically."
//     placeholder="Pre-comp Stack"
//     confirmLabel="Save"
//     onCancel={() => setOpen(false)}
//     onConfirm={async (value) => { await save(value); setOpen(false); }}
//   />

export const PromptDialog = ({
  open,
  title = 'Enter a value',
  message,
  initialValue = '',
  placeholder = '',
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  busy = false,
  onConfirm,
  onCancel,
  testIdPrefix = 'prompt-dialog',
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid={testIdPrefix}
      onClick={() => !busy && onCancel?.()}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); if (!busy) onConfirm?.(value); }}
        className="bg-surface border border-border-color rounded-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-5 border-b border-border-color">
          <Pencil size={22} weight="duotone" className="text-gold shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-text-primary" data-testid={`${testIdPrefix}-title`}>{title}</h3>
            {message && (
              <p className="text-sm text-text-secondary mt-1 whitespace-pre-line" data-testid={`${testIdPrefix}-message`}>
                {message}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testIdPrefix}-close`}
            className="text-text-secondary hover:text-text-primary disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="px-5 pt-4">
          <input
            type="text"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            disabled={busy}
            data-testid={`${testIdPrefix}-input`}
            className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none disabled:opacity-50"
          />
        </div>
        <div className="flex items-center justify-end gap-2 p-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testIdPrefix}-cancel`}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={busy || !value.trim()}
            data-testid={`${testIdPrefix}-confirm`}
            className="px-4 py-2 text-sm font-bold rounded-lg bg-gold text-black hover:bg-gold-hover disabled:opacity-50"
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PromptDialog;
